"use client";

import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Info as HelpCircle, MapPin, CircleCheck as CheckCircle2, ArrowRight, PhoneCall, Sparkles, MessageSquare, Search } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const faqs = [{
  id: "item-1",
  question: "Why is local SEO specifically important for Ipswich businesses?",
  answer: "Ipswich is a vibrant economic hub in Suffolk with rising digital competition across professional services, construction, retail, and hospitality. Local SEO ensures your business ranks at the top of Google Maps and local search results when prospective clients in Ipswich, Kesgrave, and surrounding Suffolk areas actively search for your exact services.",
  category: "Strategy & Benefits"
}, {
  id: "item-2",
  question: "Will I work directly with you, Yassine, or an agency account manager?",
  answer: "You work 100% directly with me, Yassine Essaadi. I am an independent freelance SEO consultant, not a layered agency. There are no account managers, sales reps, or outsourced junior staff. Every strategy, technical audit, content optimization, and link building tactic is executed directly by me.",
  category: "Working Together"
}, {
  id: "item-3",
  question: "How long does it take to rank on the Google Map Pack in Ipswich?",
  answer: "Initial improvements in Google Business Profile visibility and local Map Pack rankings often materialize within 30 to 60 days. Sustainable, top-3 local rankings for competitive Ipswich commercial keywords typically require 3 to 6 months of focused technical, citation, and localized link optimization.",
  category: "Timelines & Results"
}, {
  id: "item-4",
  question: "How do you tailor SEO strategies for the local Ipswich and Suffolk market?",
  answer: "I analyze search intent specific to Ipswich, Woodbridge, Felixstowe, Stowmarket, and surrounding areas. We target geo-specific keywords, implement hyper-local schema markup, optimize local citations, and generate contextual backlinks from trusted East Anglia web properties.",
  category: "Strategy & Benefits"
}, {
  id: "item-5",
  question: "What is included in an Ipswich local SEO consultancy project?",
  answer: "My comprehensive local consultancy includes a full technical website audit, Google Business Profile optimization, local citation cleanup and build, geo-targeted content creation, localized schema implementation, authoritative link building, and direct monthly performance reporting focused on qualified phone calls and leads.",
  category: "Services & Execution"
}, {
  id: "item-6",
  question: "Can local SEO help my Ipswich business compete with London firms?",
  answer: "Yes, absolutely. Google prioritizes local relevance, proximity, and structured local trust signals for geo-targeted searches. By building a hyper-focused Ipswich footprint, a dedicated local business can consistently outperform larger London agencies that lack authentic regional authority.",
  category: "Strategy & Benefits"
}, {
  id: "item-7",
  question: "How do you optimize my Google Business Profile for Ipswich searchers?",
  answer: "I optimize every component: primary and secondary category taxonomy, geotagged service area definitions, local business description copy, Google Posts cadence, Q&A sections, and a systematic review acquisition framework that builds authentic trust with Suffolk consumers.",
  category: "Services & Execution"
}, {
  id: "item-8",
  question: "Do you lock Ipswich clients into long-term agency contracts?",
  answer: "No. I believe in proving value through direct ROI and measurable search growth. I offer flexible consultancy retainer models and transparent project-based engagements without rigid 12-month agency contracts.",
  category: "Working Together"
}, {
  id: "item-9",
  question: "How does AI Search Optimisation impact local searches in Ipswich?",
  answer: "Google AI Overviews and modern AI engines pull directly from well-structured, locally verified data and top-ranking local entities. I integrate AI search optimization into every local strategy so your business is featured as the recommended local provider in AI search results.",
  category: "Modern SEO & AI"
}, {
  id: "item-10",
  question: "How do we measure success and ROI for my local business?",
  answer: "We track business-driving KPIs: direct phone call clicks, Google Maps direction requests, organic contact form submissions, and top rankings for high-intent Ipswich keywords—delivering clear, honest monthly reports without confusing vanity metrics.",
  category: "Timelines & Results"
}, {
  id: "item-11",
  question: "What makes your consultancy different from an Ipswich marketing agency?",
  answer: "Agencies frequently sell pre-packaged templates and delegate work to junior account executives while charging high overhead fees. As a freelance SEO consultant, I provide senior-level expertise, tailored strategy, total transparency, and direct one-on-one communication.",
  category: "Working Together"
}, {
  id: "item-12",
  question: "How do I get started with a free Ipswich local SEO review?",
  answer: "You can schedule a free consultation directly with me. I will conduct an initial review of your Ipswich search visibility, analyze your top regional competitors, and outline actionable opportunities during a strategy call.",
  category: "Next Steps"
}];
const categories = ["All Questions", "Strategy & Benefits", "Working Together", "Services & Execution", "Timelines & Results", "Modern SEO & AI", "Next Steps"];
export default function Ipswich() {
  const [selectedCategory, setSelectedCategory] = useState("All Questions");
  const [searchQuery, setSearchQuery] = useState("");
  const filteredFaqs = useMemo(() => {
    return faqs.filter(faq => {
      const matchesCategory = selectedCategory === "All Questions" || faq.category === selectedCategory;
      const matchesSearch = searchQuery.trim() === "" || faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);
  return <section id="ipswichlocalseofaq" data-nav="light" className="relative py-20 lg:py-28 bg-background text-foreground overflow-hidden">
      <div className="absolute top-0 right-1/4 -z-10 size-96 rounded-full bg-primary/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 left-10 -z-10 size-80 rounded-full bg-accent/10 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 gap-1.5 text-xs sm:text-sm font-medium">
            <MapPin className="size-3.5 text-primary" />
            Ipswich Local SEO Consultancy
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default mb-4">
            Frequently Asked Questions About <span className="text-primary">Local SEO in Ipswich</span>
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Clear, honest answers regarding my hands-on local SEO strategy for businesses in Ipswich and across Suffolk. You work directly with me—no account managers or agency fluff.
          </p>
        </div>

        <div className="mb-10 max-w-4xl mx-auto space-y-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-muted-foreground pointer-events-none" />
            <input type="text" placeholder="Search local SEO questions (e.g. Map Pack, timelines, Google Business Profile)..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-input bg-card text-foreground placeholder:text-muted-foreground/70 focus:outline-hidden focus:ring-2 focus:ring-primary/40 focus:border-primary text-sm sm:text-base shadow-xs transition-[border-color,box-shadow]" />
            {searchQuery && <Button variant="ghost" size="sm" onClick={() => setSearchQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground">
                Clear
              </Button>}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
            {categories.map((cat, i) => <Button key={cat} data-index={i} variant={selectedCategory === cat ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory(cat)} className={`rounded-full text-xs sm:text-sm font-medium transition-[background-color,border-color,color,box-shadow] ${selectedCategory === cat ? "bg-primary text-primary-foreground shadow-xs" : "bg-card hover:bg-muted text-foreground border-border"}`}>
                {cat}
              </Button>)}
          </div>
        </div>

        <div className="max-w-4xl mx-auto min-h-[400px]">
          {filteredFaqs.length > 0 ? <Accordion type="single" collapsible className="space-y-3">
              {filteredFaqs.map((faq, i) => <div data-index={i} key={faq.id}>
                  <AccordionItem value={faq.id} className="border border-border/80 bg-card rounded-xl px-5 sm:px-6 shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 overflow-hidden">
                    <AccordionTrigger className="py-5 hover:no-underline text-left group">
                      <div className="flex items-start gap-3 sm:gap-4 pr-2">
                        <HelpCircle className="size-5 text-primary shrink-0 mt-0.5 group-hover:scale-110 transition-transform duration-200" />
                        <span className="text-base sm:text-lg font-semibold text-primary font-default leading-snug">
                          {faq.question}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 pt-1 text-muted-foreground leading-relaxed text-sm sm:text-base pl-8 sm:pl-9">
                      <p className="mb-3">{faq.answer}</p>
                      <div className="flex items-center gap-2 pt-2 text-xs font-medium text-primary/80">
                        <CheckCircle2 className="size-3.5" />
                        <span>Direct Consultant Execution with Yassine Essaadi</span>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion> : <div className="text-center py-16 bg-card rounded-xl border border-dashed border-border">
              <HelpCircle className="size-10 text-muted-foreground mx-auto mb-3 opacity-60" />
              <h3 className="text-lg font-semibold text-foreground mb-1">No matching questions found</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Try refining your search keyword or switching categories.
              </p>
              <Button variant="outline" onClick={() => {
            setSearchQuery("");
            setSelectedCategory("All Questions");
          }}>
                Reset Filters
              </Button>
            </div>}
        </div>

        <div className="mt-16 max-w-4xl mx-auto">
          <div className="relative rounded-2xl bg-card border border-border p-6 sm:p-10 shadow-md overflow-hidden">
            <div className="absolute -right-16 -top-16 size-64 bg-primary/10 rounded-full blur-2xl pointer-events-none" />
            
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="space-y-3 text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/80 text-secondary-foreground text-xs font-medium">
                  <Sparkles className="size-3.5 text-accent" />
                  <span>Direct Consultant Access</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-foreground font-default">
                  Have a specific question about your Ipswich business?
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground max-w-xl">
                  Talk directly with me, Yassine Essaadi. No obligation, no sales pitch—just clear advice on how to improve your local search rankings in Suffolk.
                </p>

                <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-xs sm:text-sm text-foreground/80 pt-2">
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 className="size-4 text-primary" />
                    Customized Strategy
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 className="size-4 text-primary" />
                    Transparent Reporting
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 className="size-4 text-primary" />
                    Zero Agency Overhead
                  </span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row md:flex-col gap-3 w-full md:w-auto shrink-0">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-medium">
                  <Link to="/ContactPage">
                    <MessageSquare className="size-4 mr-2" />
                    Book a Free SEO Consultation
                  </Link>
                </Button>
                
                <Button asChild variant="outline" size="lg" className="border-border hover:bg-muted font-medium">
                  <Link to="/CaseStudiesPage">
                    Explore My Case Studies
                    <ArrowRight className="size-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>

            <Separator className="my-6 opacity-60" />

            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <PhoneCall className="size-3.5 text-primary" />
                <span>Direct Contact: contact@ye-consultancy.com | +44 7490984430</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Badge variant="outline" className="text-[13px] py-0">
                  YE Consultancy
                </Badge>
                <span>Swindon & Ipswich Local Consultancy Services</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}